import { useState, useMemo } from "react";
import { Radio, Building2, Users, Activity, RefreshCw, ExternalLink, ThumbsUp, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useSpokeProfiles,
  useSpokeEngagements,
  useLatestSyncLog,
  useTriggerSpokeSync,
} from "@/hooks/useSocialListeningV2";
import { formatDistanceToNow } from "date-fns";
import { openExternal } from "@/lib/utils";

/**
 * CARR Social Listening — PhantomBuster (hub-and-spoke)
 * Synced from Hub via sync_social_listening_from_hub('carr_bio').
 */
const SocialListening = () => {
  const [tab, setTab] = useState<"accounts" | "people" | "engagements">("accounts");
  const [search, setSearch] = useState("");

  const { data: accounts = [], isLoading: la } = useSpokeProfiles("company");
  const { data: people = [], isLoading: lp } = useSpokeProfiles("person");
  const { data: engagements = [], isLoading: le } = useSpokeEngagements(200);
  const { data: syncLog } = useLatestSyncLog();
  const sync = useTriggerSpokeSync("carr_bio");

  const filterByQ = (rows: any[]) => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r: any) =>
        r.profile_name?.toLowerCase().includes(q) ||
        r.prospect_name?.toLowerCase().includes(q) ||
        r.company_name?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q),
    );
  };

  const fAccounts = useMemo(() => filterByQ(accounts), [accounts, search]);
  const fPeople = useMemo(() => filterByQ(people), [people, search]);
  const fEngagements = useMemo(() => filterByQ(engagements), [engagements, search]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Radio className="h-7 w-7 text-primary" />
            Social Listening
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            PhantomBuster-powered LinkedIn signals. Monitor CARR-relevant accounts, competitors, and the sales team's owned activity — then route engagers into outreach.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {syncLog?.ended_at
              ? `Last synced ${formatDistanceToNow(new Date(syncLog.ended_at), { addSuffix: true })} · ${syncLog.profiles_synced} profiles · ${syncLog.engagements_synced} engagements`
              : "Never synced"}
          </span>
          <Button size="sm" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${sync.isPending ? "animate-spin" : ""}`} />
            {sync.isPending ? "Syncing..." : "Sync now"}
          </Button>
        </div>
      </div>

      <Input
        placeholder="Search accounts, people, engagers, companies..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="accounts" className="gap-2">
            <Building2 className="h-4 w-4" />
            Accounts ({accounts.length})
          </TabsTrigger>
          <TabsTrigger value="people" className="gap-2">
            <Users className="h-4 w-4" />
            People ({people.length})
          </TabsTrigger>
          <TabsTrigger value="engagements" className="gap-2">
            <Activity className="h-4 w-4" />
            Engagements ({engagements.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <Card>
            <CardContent className="pt-6">
              {la ? <SkelTable /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fAccounts.map((p: any) => (
                      <TableRow key={p.hub_profile_id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {p.profile_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{(p.category ?? "").replace(/_/g, " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate">{p.description}</TableCell>
                        <TableCell>
                          {p.linkedin_url && (
                            <button type="button" onClick={() => openExternal(p.linkedin_url)} title="Open LinkedIn profile" className="text-muted-foreground hover:text-foreground">
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="people">
          <Card>
            <CardContent className="pt-6">
              {lp ? <SkelTable /> : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Person</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fPeople.map((p: any) => (
                      <TableRow key={p.hub_profile_id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {p.profile_name}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-sm truncate">{p.description}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{(p.category ?? "").replace(/_/g, " ")}</Badge>
                        </TableCell>
                        <TableCell>
                          {p.linkedin_url && (
                            <button type="button" onClick={() => openExternal(p.linkedin_url)} title="Open LinkedIn profile" className="text-muted-foreground hover:text-foreground">
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagements">
          <Card>
            <CardContent className="pt-6">
              {le ? <SkelTable /> : fEngagements.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No engagements synced yet. Hit "Sync now" once PhantomBuster posts + engagers land in Hub for CARR.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Engager</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source Profile</TableHead>
                      <TableHead>Synced</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fEngagements.map((e: any) => (
                      <TableRow key={e.hub_engagement_id}>
                        <TableCell>
                          <div className="font-medium text-sm">{e.prospect_name ?? "—"}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[260px]">
                            {e.prospect_job_title ?? e.prospect_headline ?? ""}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{e.company_name ?? "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs gap-1">
                            {e.engagement_type === "comment" ? <MessageSquare className="h-3 w-3" /> : <ThumbsUp className="h-3 w-3" />}
                            {e.engagement_type ?? "engagement"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{e.source_profile_name ?? "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(e.synced_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          {e.prospect_linkedin_url && (
                            <button type="button" onClick={() => openExternal(e.prospect_linkedin_url)} title="Open LinkedIn profile" className="text-muted-foreground hover:text-foreground">
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const SkelTable = () => (
  <div className="space-y-2">
    {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
  </div>
);

export default SocialListening;
